import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CompanyInfo, ServiceTicket, InventoryItem } from '../types';
import { extractPatternDots } from '../components/PatternLockModal';
import {
  calculateTicketTotal,
  calculateTicketPartsCost,
  calculatePaymentBreakdownForPeriod,
  STATUS_LABELS,
  getTicketReportDate,
  getTicketPaymentSplit,
  getTicketPaymentSplitForPeriod,
  formatTicketPaymentMethodDisplay,
} from './storage';

// Helper for Turkish characters handling in standard jsPDF fonts
function fixTR(text: string | undefined | null): string {
  if (!text) return '';
  return String(text)
    .replace(/Ğ/g, 'G')
    .replace(/ğ/g, 'g')
    .replace(/Ü/g, 'U')
    .replace(/ü/g, 'u')
    .replace(/Ş/g, 'S')
    .replace(/ş/g, 's')
    .replace(/İ/g, 'I')
    .replace(/ı/g, 'i')
    .replace(/Ö/g, 'O')
    .replace(/ö/g, 'o')
    .replace(/Ç/g, 'C')
    .replace(/ç/g, 'c');
}

// Pure ASCII and standard formatted currency for jsPDF (never breaks font or shifts columns)
function formatPDFTL(amount: number | undefined | null): string {
  const val = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
  const parts = Math.abs(val).toFixed(2).split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1];
  const sign = val < 0 ? '-' : '';
  return `${sign}${integerPart},${decimalPart} TL`;
}

// Safe date and time formatter for PDF (no Unicode shift, clean DD.MM.YYYY HH:mm format)
function formatPDFDate(isoString?: string): string {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return fixTR(isoString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${mins}`;
  } catch {
    return fixTR(isoString);
  }
}

/**
 * Directly prints a jsPDF document via an invisible iframe without printing the background website page
 */
export function printTicketPDF(doc: jsPDF) {
  try {
    doc.autoPrint();
    const blob = doc.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = blobUrl;

    document.body.appendChild(iframe);

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        const win = window.open(blobUrl, '_blank');
        if (win) win.focus();
      }
    };

    iframe.onload = () => {
      setTimeout(triggerPrint, 300);
    };

    setTimeout(triggerPrint, 1000);

    setTimeout(() => {
      try {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      } catch {}
      URL.revokeObjectURL(blobUrl);
    }, 60000);
  } catch (err) {
    console.error('Print PDF error:', err);
    doc.save('servis_belgesi.pdf');
  }
}

/** Slightly enlarges all PDF text while preserving the existing one-page layout. */
function improveA5PrintReadability(doc: jsPDF, factor = 1.25) {
  const originalSetFontSize = doc.setFontSize.bind(doc);
  doc.setFontSize = (size: number) => originalSetFontSize(size * factor);
}

function drawFitText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  baseSize: number,
  align: 'left' | 'center' | 'right' = 'left',
  minSize = 5.5
) {
  let size = baseSize;
  doc.setFontSize(size);
  while (doc.getTextWidth(text) > maxWidth && size > minSize) {
    size = Math.max(minSize, size - 0.25);
    doc.setFontSize(size);
  }
  let fittedText = text;
  if (doc.getTextWidth(fittedText) > maxWidth) {
    while (fittedText.length > 0 && doc.getTextWidth(`${fittedText}…`) > maxWidth) fittedText = fittedText.slice(0, -1);
    fittedText = `${fittedText}…`;
  }
  doc.text(fittedText, x, y, { align });
  doc.setFontSize(baseSize);
}

function drawWrappedFitText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  maxHeight: number,
  baseSize: number,
  minSize = 4.5
) {
  let size = baseSize;
  let lines: string[] = [];
  doc.setFontSize(size);
  while (true) {
    lines = doc.splitTextToSize(text, maxWidth);
    if (doc.getTextDimensions(lines).h <= maxHeight || size <= minSize) break;
    size = Math.max(minSize, size - 0.25);
    doc.setFontSize(size);
  }
  doc.text(lines, x, y);
  doc.setFontSize(baseSize);
}

/**
 * Builds the Customer Intake Form jsPDF instance (Müşteri Kabul Formu)
 * Signature area is pinned strictly to the bottom of the A4 page (y = 252mm).
 */
export function buildTicketIntakeDoc(ticket: ServiceTicket, company: CompanyInfo): jsPDF {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });
  improveA5PrintReadability(doc);

  const pw = 210;
  const margin = 12;
  const contentWidth = pw - margin * 2; // 186mm
  const badgeW = 58;
  const badgeH = 16;
  const badgeX = pw - margin - badgeW;
  let y = 12;

  // 1. Top Header: Company info (Left) + Ticket ID Card (Right)
  let textX = margin;
  if (company.logoUrl) {
    try {
      const logoW = 32;
      const logoH = 20;
      doc.addImage(company.logoUrl, margin, y, logoW, logoH, undefined, 'FAST');
      textX = margin + logoW + 4;
    } catch (err) {
      console.warn('Could not add logo to intake PDF:', err);
      textX = margin;
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42); // slate-900
  drawFitText(doc, fixTR(company.name).toUpperCase(), textX, y + 4.5, badgeX - textX - 4, 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105); // slate-600
  drawFitText(doc, fixTR(company.address), textX, y + 10.5, badgeX - textX - 4, 9);

  let contactText = `Tel: ${fixTR(company.phone)}`;
  if (company.email) {
    contactText += `  •  E-Posta: ${fixTR(company.email)}`;
  }
  drawFitText(doc, contactText, textX, y + 15.5, badgeX - textX - 4, 9);

  // Right ID Badge
  doc.setFillColor(248, 250, 252); // slate-50
  doc.setDrawColor(226, 232, 240); // slate-200
  doc.roundedRect(badgeX, y, badgeW, badgeH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(29, 78, 216); // blue-700
  drawFitText(doc, ticket.id, badgeX + badgeW / 2, y + 6.5, badgeW - 6, 12, 'center');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139); // slate-500
  doc.text(`Tarih: ${formatPDFDate(ticket.createdAt)}`, badgeX + badgeW / 2, y + 12.5, { align: 'center' });

  y += 20;

  // Divider Line
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, pw - margin, y);
  y += 4;

  // 2. Document Title Banner
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, contentWidth, 8.5, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR('MUSTERI CIHAZ TESLIM VE KABUL TUTANAGI'), pw / 2, y + 6, { align: 'center' });

  y += 12.5;

  // 3. Customer & Device Info Grid
  const colW = (contentWidth - 6) / 2;
  const infoH = 33;

  // Customer Card (Left)
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, colW, infoH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('MUSTERI BILGILERI:'), margin + 4, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR(ticket.customerName), margin + 4, y + 11, colW - 8, 10.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  drawFitText(doc, `Tel: ${fixTR(ticket.customerPhone)}`, margin + 4, y + 17.5, colW - 8, 9);
  if (ticket.customerEmail) {
    drawFitText(doc, `E-Posta: ${fixTR(ticket.customerEmail)}`, margin + 4, y + 24, colW - 8, 9);
  }
  if (ticket.customerAddress) {
    drawFitText(doc, `Adres: ${fixTR(ticket.customerAddress)}`, margin + 4, y + 30, colW - 8, 9);
  }

  // Device Card (Right)
  const devX = margin + colW + 6;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(devX, y, colW, infoH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('CIHAZ BILGILERI:'), devX + 4, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR(`${ticket.deviceType} • ${ticket.deviceBrand} ${ticket.deviceModel}`), devX + 4, y + 11, colW - 8, 10.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  drawFitText(doc, `Seri No: ${fixTR(ticket.serialNumber || 'Belirtilmedi')}`, devX + 4, y + 18.5, colW - 8, 9);

  doc.setFont('helvetica', 'bold');
  const warrantyText = ticket.isUnderWarranty ? 'GARANTI KAPSAMINDA' : 'GARANTI DISI';
  drawFitText(doc, `Garanti Durumu: ${fixTR(warrantyText)}`, devX + 4, y + 26, colW - 8, 9);

  y += infoH + 5;

  // 4. Device Detailed Condition & Accessories (3 Columns)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR('CIHAZ DETAYLI DURUMU & TESLIM ALINAN AKSESUARLAR:'), margin, y);
  y += 2.5;

  const card3W = (contentWidth - 8) / 3;
  const card3H = 19;

  // Physical condition
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, card3W, card3H, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('Fiziksel Durum Notu:'), margin + 3, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  drawWrappedFitText(doc, fixTR(ticket.physicalCondition || 'Gozle gorulur hasar yok.'), margin + 3, y + 10, card3W - 6, card3H - 11, 9);

  // Accessories
  const accX = margin + card3W + 4;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(accX, y, card3W, card3H, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('Teslim Alinan Aksesuarlar:'), accX + 3, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  const accText = ticket.includedAccessories.length > 0
    ? ticket.includedAccessories.join(', ')
    : 'Yalniz cihaz teslim alindi.';
  drawWrappedFitText(doc, fixTR(accText), accX + 3, y + 10, card3W - 6, card3H - 11, 9);

  // Lock / Password
  const lockX = accX + card3W + 4;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(lockX, y, card3W, card3H, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('Ekran Kilit / PIN:'), lockX + 3, y + 5);

  const pwd = ticket.devicePassword || 'Yok / Belirtilmedi';
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(30, 41, 59);

  // Check if pattern lock e.g. "Desen: 1-2-5-8"
  const patternDots = extractPatternDots(pwd);
  if (patternDots.length >= 2) {
    const dots = patternDots;
    doc.text(fixTR(`Desen: ${dots.join('-')}`), lockX + 3, y + 9.5);

    // Draw a small 10mm x 10mm 3x3 pattern graph on PDF
    const pStartX = lockX + card3W - 14;
    const pStartY = y + 4;
    const pSize = 10;
    const pSpacing = pSize / 2;

    const gridPts: { [key: number]: { x: number; y: number } } = {
      1: { x: pStartX, y: pStartY },
      2: { x: pStartX + pSpacing, y: pStartY },
      3: { x: pStartX + pSize, y: pStartY },
      4: { x: pStartX, y: pStartY + pSpacing },
      5: { x: pStartX + pSpacing, y: pStartY + pSpacing },
      6: { x: pStartX + pSize, y: pStartY + pSpacing },
      7: { x: pStartX, y: pStartY + pSize },
      8: { x: pStartX + pSpacing, y: pStartY + pSize },
      9: { x: pStartX + pSize, y: pStartY + pSize },
    };

    // Draw connecting lines
    doc.setDrawColor(37, 99, 235); // blue-600
    doc.setLineWidth(0.4);
    for (let i = 1; i < dots.length; i++) {
      const p1 = gridPts[dots[i - 1]];
      const p2 = gridPts[dots[i]];
      if (p1 && p2) {
        doc.line(p1.x, p1.y, p2.x, p2.y);
      }
    }

    // Draw dots
    for (let i = 1; i <= 9; i++) {
      const pt = gridPts[i];
      const isSel = dots.includes(i);
      if (isSel) {
        doc.setFillColor(37, 99, 235);
        doc.circle(pt.x, pt.y, 0.6, 'F');
      } else {
        doc.setFillColor(148, 163, 184);
        doc.circle(pt.x, pt.y, 0.35, 'F');
      }
    }
  } else {
    drawFitText(doc, fixTR(pwd), lockX + 3, y + 10, card3W - 6, 8.5);
  }

  y += card3H + 5;

  // 5. Issue & Customer Complaint
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR('ARIZA VE MUSTERI SIKAYETI:'), margin, y);
  y += 2.5;

  const issueLines = doc.splitTextToSize(fixTR(ticket.issueDescription), contentWidth - 8);
  const issueBoxH = Math.max(14, Math.min(30, issueLines.length * 4.4 + 5));

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, contentWidth, issueBoxH, 2, 2, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(30, 41, 59);
  drawWrappedFitText(doc, fixTR(ticket.issueDescription), margin + 4, y + 5.5, contentWidth - 8, issueBoxH - 6, 9.5);

  y += issueBoxH + 4;

  // 5.3 Paid Intake / Advance Fee Banner (If present)
  const intakePaid =
    ticket.paidAmount ||
    (ticket.payments && ticket.payments.reduce((s, p) => s + (p.amount || 0), 0)) ||
    0;
  if (intakePaid > 0) {
    const intakeBoxH = 9;
    doc.setFillColor(236, 253, 245); // emerald-50
    doc.setDrawColor(167, 243, 208); // emerald-200
    doc.roundedRect(margin, y, contentWidth, intakeBoxH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(6, 95, 70); // emerald-800
    drawFitText(doc, fixTR(`TAHSIL EDILEN GIRIS / ON ODEME: ${formatPDFTL(intakePaid)}   •   Odeme Turu: ${ticket.paymentMethod || 'Nakit'}   •   Durum: Tahsil Edildi (Makbuz Kesildi)`), margin + 4, y + 5.8, contentWidth - 8, 9);
    y += intakeBoxH + 3.5;
  }

  // 6. Service Terms & Conditions
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(30, 41, 59);
  doc.text(fixTR('SERVIS KABUL VE TESLIMAT SARTLARI:'), margin, y);
  y += 2.5;

  const sigStartY = 241; // Firm bottom signature position
  const availableTermsH = Math.max(18, sigStartY - y - 6);

  const termsLines = doc.splitTextToSize(fixTR(company.terms), contentWidth - 8);
  const termsBoxH = Math.min(availableTermsH, Math.max(20, termsLines.length * 3.6 + 4));

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, contentWidth, termsBoxH, 2, 2, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(71, 85, 105);
  const maxTermsLines = Math.floor((termsBoxH - 4) / 3.6);
  doc.text(termsLines.slice(0, maxTermsLines), margin + 4, y + 4.5);

  // 7. PINNED Signatures Area
  const sigColW = (contentWidth - 30) / 2;
  const sigLeftX = margin + 5;
  const sigRightX = pw - margin - sigColW - 5;
  const sigLineY = sigStartY + 12;

  // Customer signature
  doc.setDrawColor(148, 163, 184); // slate-400
  doc.setLineWidth(0.3);
  doc.line(sigLeftX, sigLineY, sigLeftX + sigColW, sigLineY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR('MUSTERI IMZASI'), sigLeftX + sigColW / 2, sigLineY + 4.5, { align: 'center' });

  doc.setFontSize(9);
  drawFitText(doc, fixTR(ticket.customerName), sigLeftX + sigColW / 2, sigLineY + 8.5, sigColW, 9, 'center');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('Cihazimi yukarida belirtilen durumda teslim ettim.'), sigLeftX + sigColW / 2, sigLineY + 12.5, { align: 'center' });

  // Service signature
  doc.setDrawColor(148, 163, 184);
  doc.line(sigRightX, sigLineY, sigRightX + sigColW, sigLineY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR('TESLIM ALAN SERVIS YETKILISI'), sigRightX + sigColW / 2, sigLineY + 4.5, { align: 'center' });

  const intakeStaffName = ticket.createdByName || ticket.createdByUser || company.name;
  doc.setFontSize(9);
  doc.text(fixTR(intakeStaffName), sigRightX + sigColW / 2, sigLineY + 8.5, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  drawFitText(doc, fixTR(`${company.name} - Servis Yetkilisi`), sigRightX + sigColW / 2, sigLineY + 12.5, sigColW, 7.5, 'center');

  // 8. Online Tracking Banner at the VERY BOTTOM of the page (Multi-line Safe)
  const trackBoxH = 12;
  const trackY = 270;
  doc.setFillColor(239, 246, 255); // blue-50
  doc.setDrawColor(191, 219, 254); // blue-200
  doc.roundedRect(margin, trackY, contentWidth, trackBoxH, 1.5, 1.5, 'FD');

  const siteUrl = 'teknikservx.com/takip';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(29, 78, 216); // blue-700
  drawFitText(doc, fixTR(`ONLINE CIHAZ TAKIP LINKI: ${siteUrl}`), pw / 2, trackY + 4.5, contentWidth - 8, 8, 'center');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(30, 41, 59); // slate-800
  drawFitText(doc, fixTR(`Cihazinizin durumunu Form No: ${ticket.id} ve Telefon: ${ticket.customerPhone} ile sorgulayabilirsiniz.`), pw / 2, trackY + 9, contentWidth - 8, 7.5, 'center');

  return doc;
}

/**
 * Generates and downloads the Customer Intake Form PDF (Müşteri Kabul Formu)
 */
export function exportTicketIntakePDF(ticket: ServiceTicket, company: CompanyInfo) {
  const doc = buildTicketIntakeDoc(ticket, company);
  doc.save(`Musteri_Kabul_Formu_${ticket.id}.pdf`);
}

/**
 * Directly prints the Customer Intake Form PDF (Müşteri Kabul Formu)
 */
export function printTicketIntakePDF(ticket: ServiceTicket, company: CompanyInfo) {
  const doc = buildTicketIntakeDoc(ticket, company);
  printTicketPDF(doc);
}

/**
 * Builds the Delivery & Invoice Receipt jsPDF instance (Servis Faturası ve Teslimat Makbuzu)
 * Clean number formatting, no currency sign glitches, perfect column alignments.
 */
export function buildTicketInvoiceDoc(ticket: ServiceTicket, company: CompanyInfo): jsPDF {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });
  improveA5PrintReadability(doc);

  const pw = 210;
  const margin = 12;
  const contentWidth = pw - margin * 2; // 186mm
  const badgeW = 58;
  const badgeH = 16;
  const badgeX = pw - margin - badgeW;
  let y = 12;

  const { partsTotal, vatAmount, grandTotal } = calculateTicketTotal(
    ticket.parts,
    ticket.laborCost,
    ticket.vatRate,
    ticket.discount,
    ticket.isVatIncluded,
    ticket.roundingAdjustment || 0
  );

  const dateFormatted = ticket.deliveryDate
    ? formatPDFDate(ticket.deliveryDate)
    : formatPDFDate(ticket.updatedAt);

  // 1. Top Header: Company info (Left) + Ticket ID Card (Right)
  let textX = margin;
  if (company.logoUrl) {
    try {
      const logoW = 32;
      const logoH = 20;
      doc.addImage(company.logoUrl, margin, y, logoW, logoH, undefined, 'FAST');
      textX = margin + logoW + 4;
    } catch (err) {
      console.warn('Could not add logo to invoice PDF:', err);
      textX = margin;
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR(company.name).toUpperCase(), textX, y + 4.5, badgeX - textX - 4, 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  drawFitText(doc, fixTR(company.address), textX, y + 10.5, badgeX - textX - 4, 9);

  let contactText = `Tel: ${fixTR(company.phone)}`;
  if (company.email) {
    contactText += `  •  E-Posta: ${fixTR(company.email)}`;
  }
  drawFitText(doc, contactText, textX, y + 15.5, badgeX - textX - 4, 9);

  // Right ID Badge
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(badgeX, y, badgeW, badgeH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(29, 78, 216);
  drawFitText(doc, ticket.id, badgeX + badgeW / 2, y + 6.5, badgeW - 6, 12, 'center');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(`Tarih: ${dateFormatted}`, badgeX + badgeW / 2, y + 12.5, { align: 'center' });

  y += 20;

  // Divider Line
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, pw - margin, y);
  y += 4;

  // 2. Document Title Banner
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, contentWidth, 8.5, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR('SERVIS ISLEM FATURASI VE TESLIMAT MAKBUZU'), pw / 2, y + 6, contentWidth - 8, 10.5, 'center');

  y += 12.5;

  // 3. Customer & Device Info Grid
  const colW = (contentWidth - 6) / 2;
  const infoH = 33;

  // Customer Card (Left)
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, colW, infoH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('MUSTERI BILGILERI:'), margin + 4, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR(ticket.customerName), margin + 4, y + 11, colW - 8, 10.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  drawFitText(doc, `Tel: ${fixTR(ticket.customerPhone)}`, margin + 4, y + 17.5, colW - 8, 9);
  if (ticket.customerEmail) {
    drawFitText(doc, `E-Posta: ${fixTR(ticket.customerEmail)}`, margin + 4, y + 24, colW - 8, 9);
  }
  if (ticket.customerAddress) {
    drawFitText(doc, `Adres: ${fixTR(ticket.customerAddress)}`, margin + 4, y + 30, colW - 8, 9);
  }

  // Device Card (Right)
  const devX = margin + colW + 6;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(devX, y, colW, infoH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('CIHAZ BILGILERI:'), devX + 4, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR(`${ticket.deviceType} • ${ticket.deviceBrand} ${ticket.deviceModel}`), devX + 4, y + 11, colW - 8, 10.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  drawFitText(doc, `Seri No: ${fixTR(ticket.serialNumber || 'Belirtilmedi')}`, devX + 4, y + 18.5, colW - 8, 9);

  doc.setFont('helvetica', 'bold');
  const warrantyText = ticket.isUnderWarranty ? 'GARANTI KAPSAMINDA' : 'GARANTI DISI';
  drawFitText(doc, `Garanti Durumu: ${fixTR(warrantyText)}`, devX + 4, y + 26, colW - 8, 9);

  y += infoH + 5;

  // 4. Parts & Services Table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR('YAPILAN ISLEMLER VE DEGISEN PARCALAR:'), margin, y);
  y += 2.5;

  const tableBody: (string | number)[][] = [];

  ticket.parts.forEach((p, idx) => {
    tableBody.push([
      idx + 1,
      fixTR(`Yedek Parca: ${p.name}`),
      p.quantity,
      formatPDFTL(p.unitPrice),
      formatPDFTL(p.quantity * p.unitPrice),
    ]);
  });

  if (ticket.laborCost > 0) {
    tableBody.push([
      ticket.parts.length + 1,
      fixTR('Iscilik'),
      1,
      formatPDFTL(ticket.laborCost),
      formatPDFTL(ticket.laborCost),
    ]);
  }

  if (tableBody.length === 0) {
    tableBody.push(['1', fixTR('Servis Kontrol ve Tespit'), 1, formatPDFTL(0), formatPDFTL(0)]);
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    theme: 'plain',
    headStyles: {
      fillColor: [241, 245, 249],
      textColor: [51, 65, 85],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 2.8,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 2.8,
      textColor: [30, 41, 59],
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 16, halign: 'center' },
      3: { cellWidth: 32, halign: 'right' },
      4: { cellWidth: 34, halign: 'right', fontStyle: 'bold' },
    },
    head: [[
      '#',
      fixTR('Aciklama / Parca Adi'),
      fixTR('Adet'),
      fixTR('Birim Fiyat'),
      fixTR('Toplam'),
    ]],
    body: tableBody,
  });

  // @ts-expect-error autoTable lastAutoTable
  y = doc.lastAutoTable.finalY + 5;

  // 5. Payment Details Box (Left) & Financial Totals (Right)
  const split = getTicketPaymentSplit(ticket);
  const totalPaid = split.total;
  const remainingDebt = Math.max(0, grandTotal - totalPaid);

  const blockW = (contentWidth - 8) / 2;
  const blockH = 43;

  // Left: Payment status box
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, blockW, blockH, 2, 2, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.text(fixTR('ODEME VE TAHSILAT DETAYI:'), margin + 4, y + 5.5);

  const paymentStatusText =
    ticket.paymentStatus === 'KismiOdendi' || (totalPaid > 0 && remainingDebt > 0)
      ? 'Kismi Odendi'
      : ticket.paymentStatus === 'Odendi' || (totalPaid >= grandTotal && grandTotal > 0)
      ? 'Tam Odendi'
      : 'Odenmedi';

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(51, 65, 85);
  doc.text(`Odeme Durumu: `, margin + 4, y + 12);
  doc.setFont('helvetica', 'bold');
  drawFitText(doc, fixTR(paymentStatusText).toUpperCase(), margin + 42, y + 12, blockW - 46, 9);

  doc.setFont('helvetica', 'normal');
  doc.text(`Alinan Tutar: `, margin + 4, y + 18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(4, 120, 87);
  drawFitText(doc, formatPDFTL(totalPaid), margin + 42, y + 18, blockW - 46, 9);

  if (remainingDebt > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.text(`Kalan Borc: `, margin + 4, y + 24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(190, 18, 60);
    drawFitText(doc, formatPDFTL(remainingDebt), margin + 42, y + 24, blockW - 46, 9);
  }

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  doc.text(`Odeme Yontemi: `, margin + 4, y + 30);
  doc.setFont('helvetica', 'bold');
  drawFitText(doc, fixTR(formatTicketPaymentMethodDisplay(ticket)), margin + 42, y + 30, blockW - 46, 9);

  doc.setFont('helvetica', 'normal');
  doc.text(`Servis Durumu: `, margin + 4, y + 36);
  doc.setFont('helvetica', 'bold');
  drawFitText(doc, fixTR(STATUS_LABELS[ticket.status]), margin + 42, y + 36, blockW - 46, 9);

  // Right: Totals Box
  const totX = margin + blockW + 8;
  let totY = y + 4;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('Yedek Parca Toplami:'), totX, totY);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(formatPDFTL(partsTotal), pw - margin, totY, { align: 'right' });
  totY += 5.5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(fixTR('Alinan Ucret:'), totX, totY);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(formatPDFTL(ticket.laborCost), pw - margin, totY, { align: 'right' });
  totY += 5.5;

  if (ticket.discount > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(225, 29, 72);
    doc.text(fixTR('Iskonto / Indirim:'), totX, totY);
    doc.setFont('helvetica', 'bold');
    doc.text(`-${formatPDFTL(ticket.discount)}`, pw - margin, totY, { align: 'right' });
    totY += 5.5;
  }

  if (ticket.vatRate > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(fixTR(`KDV (%20):`), totX, totY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(formatPDFTL(vatAmount), pw - margin, totY, { align: 'right' });
    totY += 5.5;
  }

  // Grand total divider & text
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(0.4);
  doc.line(totX, totY, pw - margin, totY);
  totY += 5.5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR('GENEL TOPLAM:'), totX, totY);

  doc.setFontSize(12);
  doc.setTextColor(29, 78, 216);
  doc.text(formatPDFTL(grandTotal), pw - margin, totY, { align: 'right' });

  y += blockH + 5;

  // 6. Performed Actions / Service Operations (Customer-facing, technicianNotes is strictly hidden)
  if (ticket.performedActions) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text(fixTR('YAPILAN ISLEMLER:'), margin, y);
    y += 2.5;

    const actionLines = doc.splitTextToSize(fixTR(ticket.performedActions), contentWidth - 8);
    const actionBoxH = Math.max(12, actionLines.length * 4.2 + 5);

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(margin, y, contentWidth, actionBoxH, 2, 2, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    doc.text(actionLines, margin + 4, y + 5);

    y += actionBoxH + 5;
  }

  // 7. Warranty Note at bottom (Centered)
  const warrantyDays = ticket.warrantyDays !== undefined ? ticket.warrantyDays : 30;
  const warrantyNote =
    warrantyDays === 0
      ? 'Yapilan islem servis garantisi kapsami disindadir.'
      : `Yapilan islem teslim tarihinden itibaren ${warrantyDays} gun servis garantimiz altindadir.`;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  drawFitText(doc, fixTR(warrantyNote), pw / 2, y + 4, contentWidth - 8, 8.5, 'center');

  // 8. Signatures for Service Delivery (Teslim Alan Müşteri & Teslim Eden / Kayıt Alan Servis Yetkilisi)
  const sigStartY = 241;
  const sigColW = (contentWidth - 30) / 2;
  const sigLeftX = margin + 5;
  const sigRightX = pw - margin - sigColW - 5;
  const sigLineY = sigStartY + 12;

  // Customer Delivery Signature
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.3);
  doc.line(sigLeftX, sigLineY, sigLeftX + sigColW, sigLineY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR('TESLIM ALAN MUSTERI'), sigLeftX + sigColW / 2, sigLineY + 4.5, sigColW, 9.5, 'center');

  doc.setFontSize(9);
  drawFitText(doc, fixTR(ticket.customerName), sigLeftX + sigColW / 2, sigLineY + 8.5, sigColW, 9, 'center');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  drawFitText(doc, fixTR('Cihazimi eksiksiz ve calisir durumda teslim aldim.'), sigLeftX + sigColW / 2, sigLineY + 12.5, sigColW, 7.5, 'center');

  // Service Deliverer / Ticket Creator
  doc.setDrawColor(148, 163, 184);
  doc.line(sigRightX, sigLineY, sigRightX + sigColW, sigLineY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(15, 23, 42);
  drawFitText(doc, fixTR('TESLIM EDEN / SERVIS YETKILISI'), sigRightX + sigColW / 2, sigLineY + 4.5, sigColW, 9.5, 'center');

  const invoiceStaffName =
    ticket.deliveredByName ||
    ticket.repairedByName ||
    ticket.createdByName ||
    ticket.createdByUser ||
    company.name;
  doc.setFontSize(9);
  drawFitText(doc, fixTR(invoiceStaffName), sigRightX + sigColW / 2, sigLineY + 8.5, sigColW, 9, 'center');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  drawFitText(doc, fixTR(`${company.name} - Servis Yetkilisi`), sigRightX + sigColW / 2, sigLineY + 12.5, sigColW, 7.5, 'center');

  // 9. Online Tracking Banner at the VERY BOTTOM of the page (Multi-line Safe)
  const trackBoxH = 12;
  const trackY = 270;
  doc.setFillColor(239, 246, 255); // blue-50
  doc.setDrawColor(191, 219, 254); // blue-200
  doc.roundedRect(margin, trackY, contentWidth, trackBoxH, 1.5, 1.5, 'FD');

  return doc;
}

/**
 * Generates and downloads the Delivery & Invoice Receipt PDF (Servis Faturası ve Teslimat Makbuzu)
 */
export function exportTicketInvoicePDF(ticket: ServiceTicket, company: CompanyInfo) {
  const doc = buildTicketInvoiceDoc(ticket, company);
  doc.save(`Servis_Faturasi_${ticket.id}.pdf`);
}

/**
 * Directly prints the Delivery & Invoice Receipt PDF (Servis Faturası ve Teslimat Makbuzu)
 */
export function printTicketInvoicePDF(ticket: ServiceTicket, company: CompanyInfo) {
  const doc = buildTicketInvoiceDoc(ticket, company);
  printTicketPDF(doc);
}

/**
 * Monthly Report PDF Export
 */
export function exportMonthlyCollectionsReportPDF(
  monthKey: string,
  monthName: string,
  tickets: ServiceTicket[],
  company: CompanyInfo
) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const eligible = tickets.filter(t => !t.isDeleted && t.serviceType !== 'Sozlesmeli' && !t.contractedCompanyId);
  const rows: Array<[string, string, string, string, string, string]> = [];
  let total = 0;
  const totals = { cash: 0, card: 0, transfer: 0 };
  eligible.forEach(ticket => {
    const explicit = Array.isArray(ticket.payments) ? ticket.payments : [];
    if (explicit.length) {
      explicit.forEach(payment => {
        const amount = Math.max(0, Number(payment.amount) || 0);
        if (amount <= 0 || !payment.date?.startsWith(monthKey)) return;
        const method = String(payment.method || 'Nakit');
        if (method.toLocaleLowerCase('tr-TR').includes('kart') || method.toLocaleLowerCase('tr-TR').includes('pos')) totals.card += amount;
        else if (method.toLocaleLowerCase('tr-TR').includes('eft') || method.toLocaleLowerCase('tr-TR').includes('havale')) totals.transfer += amount;
        else totals.cash += amount;
        total += amount;
        rows.push([
          fixTR(formatPDFDate(payment.date)), fixTR(ticket.id), fixTR(ticket.customerName || '-'),
          fixTR(`${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim() || '-'),
          fixTR(method), formatPDFTL(amount),
        ]);
      });
      return;
    }
    if (!ticket.paymentDate?.startsWith(monthKey)) return;
    const split = getTicketPaymentSplitForPeriod(ticket, monthKey);
    const parts: Array<[string, number]> = [['Nakit', split.cash], ['Kredi Karti', split.card], ['EFT/Havale', split.transfer]];
    parts.forEach(([method, amount]) => {
      if (amount <= 0) return;
      if (method === 'Kredi Karti') totals.card += amount;
      else if (method === 'EFT/Havale') totals.transfer += amount;
      else totals.cash += amount;
      total += amount;
      rows.push([
        fixTR(formatPDFDate(ticket.paymentDate)), fixTR(ticket.id), fixTR(ticket.customerName || '-'),
        fixTR(`${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim() || '-'),
        fixTR(method), formatPDFTL(amount),
      ]);
    });
  });
  rows.sort((a, b) => a[0].localeCompare(b[0]));

  doc.setFillColor(30, 41, 59);
  doc.rect(10, 12, 277, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(fixTR(company.name).toUpperCase(), 16, 21);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(fixTR(`AYLIK TAHSILAT RAPORU (${monthName.toUpperCase()})`), 16, 29);

  autoTable(doc, {
    startY: 40, margin: { left: 10, right: 10 }, theme: 'grid',
    head: [[fixTR('NAKIT'), fixTR('KREDI KARTI / POS'), fixTR('HAVALE / EFT'), fixTR('TOPLAM TAHSILAT')]],
    body: [[formatPDFTL(totals.cash), formatPDFTL(totals.card), formatPDFTL(totals.transfer), formatPDFTL(total)]],
    headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold', halign: 'center' },
    styles: { fontSize: 9, cellPadding: 3, halign: 'center' },
  });
  autoTable(doc, {
    startY: (doc as any).lastAutoTable.finalY + 7, margin: { left: 10, right: 10 }, theme: 'striped',
    head: [[fixTR('ODEME TARIHI'), fixTR('SERVIS NO'), fixTR('MUSTERI'), fixTR('CIHAZ'), fixTR('ODEME TURU'), fixTR('TUTAR')]],
    body: rows.length ? rows : [[fixTR('-'), fixTR('-'), fixTR('Bu ay tahsilat kaydi bulunmuyor.'), '', '', formatPDFTL(0)]],
    headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold', fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
    columnStyles: { 0: { cellWidth: 29 }, 1: { cellWidth: 27 }, 2: { cellWidth: 52 }, 3: { cellWidth: 72 }, 4: { cellWidth: 42 }, 5: { cellWidth: 45, halign: 'right' } },
  });
  doc.save(`Aylik_Tahsilat_Raporu_${monthKey}.pdf`);
}

export function exportMonthlyReportPDF(
  monthKey: string,
  monthName: string,
  tickets: ServiceTicket[],
  company: CompanyInfo
) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pw = 210;
  let y = 14;

  const eligibleTickets = tickets.filter(t =>
    !t.isDeleted && t.serviceType !== 'Sozlesmeli' && !t.contractedCompanyId
  );
  const monthTickets = eligibleTickets.filter(t => Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(monthKey)));

  let totalRevenue = 0;
  let totalVat = 0;
  let totalExVatRevenue = 0;
  let totalPartsCost = 0;
  let totalSubServiceCost = 0;
  let totalCollected = 0;
  let totalRemaining = 0;
  let deliveredCount = 0;
  let readyCount = 0;

  monthTickets.forEach(t => {
    const isServiceMonth = Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(monthKey));
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const partsCost = calculateTicketPartsCost(t.parts);
    const subCost = t.subServiceCost || 0;
    const paid = getTicketPaymentSplitForPeriod(t, monthKey).total;
    const recognizedRatio = 1;
    const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);

    totalRevenue += grandTotal;
    totalVat += vatAmount;
    totalExVatRevenue += subtotal;
    totalPartsCost += partsCost;
    totalSubServiceCost += subCost;
    if (isServiceMonth) {
      totalRemaining += remaining;
      if (t.status === 'TeslimEdildi') deliveredCount++;
      else if (t.status === 'Hazir') readyCount++;
    }
    totalCollected += paid;
  });

  // Finansal gelir ve KDV, bu ay gerçekten alınan ödemelere göre kaydedilir.
  totalRevenue = 0;
  totalVat = 0;
  totalExVatRevenue = 0;
  eligibleTickets.forEach(t => {
    const received = getTicketPaymentSplitForPeriod(t, monthKey).total;
    if (received <= 0) return;
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const ratio = grandTotal > 0 ? received / grandTotal : 0;
    totalRevenue += received;
    totalVat += vatAmount * ratio;
    totalExVatRevenue += subtotal * ratio;
  });

  const totalCost = totalPartsCost + totalSubServiceCost;
  const grossProfit = totalRevenue - totalCost;
  const netProfitExVat = totalExVatRevenue - totalCost; // KDV & Alt Servis Düşülmüş Net Kâr
  const payBreakdown = calculatePaymentBreakdownForPeriod(eligibleTickets, monthKey);
  totalCollected = payBreakdown.totalCollected;

  // Title Banner
  doc.setFillColor(30, 41, 59);
  doc.rect(10, y, pw - 20, 22, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(fixTR(company.name).toUpperCase(), 15, y + 8);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(fixTR(`AYLIK SERVIS, KDV, TAHSILAT VE NET KAR RAPORU (${monthName.toUpperCase()})`), 15, y + 16);

  y += 26;

  // Executive Summary Cards (Table 1: Sales & Tax & Profit, Table 2: Cashflow)
  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold', halign: 'center', fontSize: 7.5 },
    styles: { fontSize: 7.5, cellPadding: 2, font: 'helvetica', halign: 'center' },
    head: [[
      fixTR('TOPLAM KAYIT'),
      fixTR('TESLIM'),
      fixTR('AY ICINDE ALINAN'),
      fixTR('ALINAN TAHSILATIN KDVSI'),
      fixTR('ALINAN TAHSILAT KDV HARIC'),
      fixTR('PARCA MALIYET'),
      fixTR('AYIN SERVIS FORMLARINDAN ALINACAK'),
      fixTR('KDV DUSULMUS NET KAR')
    ]],
    body: [[
      monthTickets.length,
      `${deliveredCount} Adet`,
      formatPDFTL(totalRevenue),
      formatPDFTL(totalVat),
      formatPDFTL(totalExVatRevenue),
      formatPDFTL(totalPartsCost),
      formatPDFTL(totalRemaining),
      formatPDFTL(netProfitExVat)
    ]],
  });

  // @ts-expect-error autoTable lastAutoTable
  y = doc.lastAutoTable.finalY + 4;

  // Table 2: Payment Method Breakdown (Nakit, Kredi Kartı, Havale)
  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    headStyles: { fillColor: [79, 70, 229], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 7.5 },
    styles: { fontSize: 7.5, cellPadding: 2, font: 'helvetica', halign: 'center' },
    head: [[
      fixTR('TOPLAM TAHSILAT'),
      fixTR('NAKIT TAHSILAT'),
      fixTR('KREDI KARTI / POS'),
      fixTR('HAVALE / EFT')
    ]],
    body: [[
      formatPDFTL(payBreakdown.totalCollected),
      `${formatPDFTL(payBreakdown.cash.amount)} (${payBreakdown.cash.count} Adet)`,
      `${formatPDFTL(payBreakdown.card.amount)} (${payBreakdown.card.count} Adet)`,
      `${formatPDFTL(payBreakdown.transfer.amount)} (${payBreakdown.transfer.count} Adet)`
    ]],
  });

  // @ts-expect-error autoTable lastAutoTable
  y = doc.lastAutoTable.finalY + 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(71, 85, 105);
  doc.text(fixTR('Gelir ve KDV secilen ayda alinan odemelere gore; maliyetler servis islem ayina gore hesaplanir.'), 10, y);
  y += 5;

  // Monthly Tickets Table
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR(`SERVIS, KDV VE NET KAR DETAYLARI (${monthTickets.length} Adet Kayit)`), 10, y);
  y += 3;

  const tableRows = monthTickets.map(t => {
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const pCost = calculateTicketPartsCost(t.parts);
    const periodPayment = getTicketPaymentSplitForPeriod(t, monthKey);
    const paid = periodPayment.total;
    const recognizedRatio = 1;
    const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
    const paymentRatio = grandTotal > 0 ? paid / grandTotal : 0;
    const recognizedSubtotal = subtotal * paymentRatio;
    const recognizedVat = vatAmount * paymentRatio;
    const recognizedPartCost = pCost;
    const recognizedSubCost = t.subServiceCost || 0;
    const netProfit = recognizedSubtotal - recognizedPartCost - recognizedSubCost;
    const methods = [
      periodPayment.cash > 0 ? 'Nakit' : '',
      periodPayment.card > 0 ? 'Kredi Karti' : '',
      periodPayment.transfer > 0 ? 'EFT/Havale' : '',
    ].filter(Boolean);
    const methodStr = methods.length > 0 ? ` (${methods.join(' + ')})` : '';

    return [
      t.id,
      fixTR(t.customerName),
      fixTR(`${t.deviceType} ${t.deviceBrand}`),
      fixTR(STATUS_LABELS[t.status] || t.status),
      formatPDFTL(grandTotal),
      formatPDFTL(recognizedVat),
      formatPDFTL(recognizedPartCost),
      `${formatPDFTL(paid)}${fixTR(methodStr)}`,
      formatPDFTL(remaining),
      formatPDFTL(netProfit),
    ];
  });

  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'striped',
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    styles: { fontSize: 7, cellPadding: 1.8, font: 'helvetica' },
    columnStyles: {
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
      9: { halign: 'right', fontStyle: 'bold' },
    },
    head: [[
      fixTR('SERVIS NO'),
      fixTR('MUSTERI'),
      fixTR('CIHAZ'),
      fixTR('DURUM'),
      fixTR('FATURA TOPLAMI'),
      fixTR('KDV'),
      fixTR('MALIYET'),
      fixTR('TAHSILAT'),
      fixTR('ALACAK'),
      fixTR('NET KAR')
    ]],
    body: tableRows.length > 0 ? tableRows : [[fixTR('Kayit Bulunamadi'), '', '', '', '', '', '', '', '', '']],
  });

  const monthlyReceipts: Array<[string, string, string, string, string, string]> = [];
  eligibleTickets.forEach(ticket => {
    const payments = Array.isArray(ticket.payments) ? ticket.payments : [];
    if (payments.length) {
      payments.forEach(payment => {
        const amount = Math.max(0, Number(payment.amount) || 0);
        if (amount > 0 && payment.date?.startsWith(monthKey)) monthlyReceipts.push([
          fixTR(formatPDFDate(payment.date)), fixTR(ticket.id), fixTR(ticket.customerName || '-'),
          fixTR(`${ticket.deviceType || ''} ${ticket.deviceBrand || ''}`.trim()), fixTR(String(payment.method || 'Nakit')), formatPDFTL(amount),
        ]);
      });
      return;
    }
    if (!ticket.paymentDate?.startsWith(monthKey)) return;
    const split = getTicketPaymentSplitForPeriod(ticket, monthKey);
    ( [['Nakit', split.cash], ['Kredi Karti', split.card], ['EFT/Havale', split.transfer]] as Array<[string, number]> ).forEach(([method, amount]) => {
      if (amount > 0) monthlyReceipts.push([fixTR(formatPDFDate(ticket.paymentDate)), fixTR(ticket.id), fixTR(ticket.customerName || '-'), fixTR(`${ticket.deviceType || ''} ${ticket.deviceBrand || ''}`.trim()), fixTR(method), formatPDFTL(amount)]);
    });
  });
  let receiptTableStartY = (doc as any).lastAutoTable.finalY + 8;
  if (receiptTableStartY > 270) { doc.addPage(); receiptTableStartY = 14; }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(fixTR(`AYLIK TAHSILAT DETAYLARI - TOPLAM ${formatPDFTL(totalCollected)}`), 10, receiptTableStartY);
  autoTable(doc, {
    startY: receiptTableStartY + 3, margin: { left: 10, right: 10 }, theme: 'striped',
    headStyles: { fillColor: [5, 150, 105], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5 },
    styles: { fontSize: 7, cellPadding: 1.8, font: 'helvetica' },
    head: [[fixTR('ODEME TARIHI'), fixTR('SERVIS FORMU'), fixTR('MUSTERI'), fixTR('CIHAZ'), fixTR('ODEME TURU'), fixTR('ALINAN TUTAR')]],
    body: monthlyReceipts.length ? monthlyReceipts : [[fixTR(monthKey), '', fixTR('Bu ay tahsilat kaydi bulunmuyor.'), '', '', formatPDFTL(0)]],
  });

  doc.save(`Aylik_Servis_Raporu_${monthKey}.pdf`);
}

/**
 * Daily Report PDF Export (Gunluk Servis, Teslimat, Tahsilat ve Alacak Raporu)
 */
export function exportDailyReportPDF(
  dateKey: string,
  formattedDateName: string,
  tickets: ServiceTicket[],
  company: CompanyInfo
) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pw = 210;
  let y = 14;

  const eligibleTickets = tickets.filter(t =>
    !t.isDeleted && t.serviceType !== 'Sozlesmeli' && !t.contractedCompanyId
  );
  const dayTickets = eligibleTickets.filter(
    t =>
      Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(dateKey))
  );

  let totalRevenue = 0;
  let totalVat = 0;
  let totalExVatRevenue = 0;
  let totalPartsCost = 0;
  let totalSubServiceCost = 0;
  let totalCollected = 0;
  let totalRemaining = 0;
  let deliveredCount = 0;

  dayTickets.forEach(t => {
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const partsCost = calculateTicketPartsCost(t.parts);
    const subCost = t.subServiceCost || 0;

    const paid = getTicketPaymentSplitForPeriod(t, dateKey).total;
    const allTimePaid = getTicketPaymentSplit(t).total;
    const remaining = Math.max(0, grandTotal - allTimePaid);
    const recognizedRatio = 1;

    totalRevenue += grandTotal;
    totalVat += vatAmount;
    totalExVatRevenue += subtotal;
    totalPartsCost += partsCost;
    totalSubServiceCost += subCost;
    totalRemaining += remaining;

    if (t.status === 'TeslimEdildi') {
      deliveredCount++;
    }
  });

  // Günlük ciro, seçilen gün tahsil edilen gerçek ödeme toplamıdır.
  totalRevenue = 0;
  totalVat = 0;
  totalExVatRevenue = 0;
  eligibleTickets.forEach(t => {
    const received = getTicketPaymentSplitForPeriod(t, dateKey).total;
    if (received <= 0) return;
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const ratio = grandTotal > 0 ? received / grandTotal : 0;
    totalRevenue += received;
    totalVat += vatAmount * ratio;
    totalExVatRevenue += subtotal * ratio;
  });

  const payBreakdown = calculatePaymentBreakdownForPeriod(eligibleTickets, dateKey);
  totalCollected = payBreakdown.totalCollected;

  const totalCost = totalPartsCost + totalSubServiceCost;
  const grossProfit = totalRevenue - totalCost;
  const netProfitExVat = totalExVatRevenue - totalCost; // KDV & Alt Servis Düşülmüş Net Kâr

  // Title Banner
  doc.setFillColor(15, 23, 42);
  doc.rect(10, y, pw - 20, 22, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(fixTR(company.name).toUpperCase(), 15, y + 8);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(fixTR(`GUNLUK SERVIS, KDV, TAHSILAT VE NET KAR RAPORU (${formattedDateName.toUpperCase()})`), 15, y + 16);

  y += 26;

  // Executive Summary Cards
  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold', halign: 'center', fontSize: 7.5 },
    styles: { fontSize: 7.5, cellPadding: 2, font: 'helvetica', halign: 'center' },
    head: [[
      fixTR('TOPLAM KAYIT'),
      fixTR('TESLIM'),
      fixTR('ALINAN TAHSILAT'),
      fixTR('KDV TUTARI'),
      fixTR('KDV HARIC MATRAH'),
      fixTR('PARCA MALIYET'),
      fixTR('TAHSILAT (ALINAN)'),
      fixTR('KALAN ALACAK'),
      fixTR('KDV DUSULMUS NET KAR')
    ]],
    body: [[
      dayTickets.length,
      `${deliveredCount} Adet`,
      formatPDFTL(totalRevenue),
      formatPDFTL(totalVat),
      formatPDFTL(totalExVatRevenue),
      formatPDFTL(totalPartsCost),
      formatPDFTL(totalCollected),
      formatPDFTL(totalRemaining),
      formatPDFTL(netProfitExVat)
    ]],
  });

  // @ts-expect-error autoTable lastAutoTable
  y = doc.lastAutoTable.finalY + 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(71, 85, 105);
  doc.text(fixTR('Form ucretleri son servis islem gunune, tahsilatlar paranin alindigi gune gore hesaplanir.'), 10, y);
  y += 5;

  // Daily Tickets Table
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR(`GUNLUK SERVIS, KDV VE NET KAR DETAYLARI (${dayTickets.length} Adet Kayit)`), 10, y);
  y += 3;

  const tableRows = dayTickets.map(t => {
    const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
    const pCost = calculateTicketPartsCost(t.parts);
    const paid = getTicketPaymentSplitForPeriod(t, dateKey).total;
    const allTimePaid = getTicketPaymentSplit(t).total;
    const remaining = Math.max(0, grandTotal - allTimePaid);
    const recognizedRatio = 1;
    const recognizedVat = vatAmount;
    const recognizedSubtotal = subtotal;
    const recognizedCost = pCost;
    const netProfit = recognizedSubtotal - recognizedCost;

    return [
      t.id,
      fixTR(t.customerName),
      fixTR(`${t.deviceType} ${t.deviceBrand}`),
      fixTR(STATUS_LABELS[t.status] || t.status),
      formatPDFTL(grandTotal),
      formatPDFTL(recognizedVat),
      formatPDFTL(recognizedCost),
      formatPDFTL(paid),
      formatPDFTL(remaining),
      formatPDFTL(netProfit),
    ];
  });

  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'striped',
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    styles: { fontSize: 7, cellPadding: 1.8, font: 'helvetica' },
    columnStyles: {
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
      9: { halign: 'right', fontStyle: 'bold' },
    },
    head: [[
      fixTR('SERVIS NO'),
      fixTR('MUSTERI'),
      fixTR('CIHAZ'),
      fixTR('DURUM'),
      fixTR('TOPLAM'),
      fixTR('KDV'),
      fixTR('MALIYET'),
      fixTR('TAHSILAT'),
      fixTR('ALACAK'),
      fixTR('NET KAR')
    ]],
    body: tableRows.length > 0 ? tableRows : [[fixTR('Bu tarihe ait servis kaydi bulunmuyor.'), '', '', '', '', '', '', '', '', '']],
  });

  const collectionRows: Array<[string, string, string, string, string]> = [];
  eligibleTickets.forEach(ticket => {
    const payments = Array.isArray(ticket.payments) ? ticket.payments : [];
    if (payments.length) {
      payments.forEach(payment => {
        const amount = Math.max(0, Number(payment.amount) || 0);
        if (amount <= 0 || !payment.date?.startsWith(dateKey)) return;
        collectionRows.push([
          fixTR(formatPDFDate(payment.date)), fixTR(ticket.id), fixTR(ticket.customerName || '-'),
          fixTR(String(payment.method || 'Nakit')), formatPDFTL(amount),
        ]);
      });
      return;
    }
    if (!ticket.paymentDate?.startsWith(dateKey)) return;
    const split = getTicketPaymentSplitForPeriod(ticket, dateKey);
    const methods: Array<[string, number]> = [['Nakit', split.cash], ['Kredi Karti', split.card], ['EFT/Havale', split.transfer]];
    methods.forEach(([method, amount]) => {
      if (amount <= 0) return;
      collectionRows.push([
        fixTR(formatPDFDate(ticket.paymentDate)), fixTR(ticket.id), fixTR(ticket.customerName || '-'),
        fixTR(method), formatPDFTL(amount),
      ]);
    });
  });
  collectionRows.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
  const collectionTotal = calculatePaymentBreakdownForPeriod(eligibleTickets, dateKey).totalCollected;
  let collectionStartY = (doc as any).lastAutoTable.finalY + 8;
  if (collectionStartY > 270) { doc.addPage(); collectionStartY = 14; }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR(`GUNLUK TAHSILAT DETAYLARI - TOPLAM ${formatPDFTL(collectionTotal)}`), 10, collectionStartY);
  autoTable(doc, {
    startY: collectionStartY + 3,
    margin: { left: 10, right: 10 },
    theme: 'striped',
    headStyles: { fillColor: [5, 150, 105], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 2, font: 'helvetica' },
    columnStyles: { 4: { halign: 'right', fontStyle: 'bold' } },
    head: [[fixTR('ODEME TARIHI'), fixTR('SERVIS FORMU'), fixTR('MUSTERI'), fixTR('ODEME TURU'), fixTR('ALINAN TUTAR')]],
    body: collectionRows.length ? collectionRows : [[fixTR(dateKey), '', fixTR('Bu gun tahsilat kaydi bulunmuyor.'), '', formatPDFTL(0)]],
  });

  doc.save(`Gunluk_Servis_Raporu_${dateKey}.pdf`);
}

export function exportInventoryPDF(
  items: any[],
  companyInfo: CompanyInfo
) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const totalKinds = items.length;
  const totalUnits = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const totalCostVal = items.reduce((s, i) => s + (i.quantity || 0) * (i.costPrice || 0), 0);
  const totalSaleVal = items.reduce((s, i) => s + (i.quantity || 0) * (i.salePrice || 0), 0);
  const criticalCount = items.filter(i => (i.quantity || 0) <= (i.minQuantity || 2) && (i.quantity || 0) > 0).length;
  const outOfStockCount = items.filter(i => (i.quantity || 0) <= 0).length;

  // Header banner
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, 297, 24, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.text(fixTR(companyInfo.name || 'TEKNIK SERVIS'), 14, 11);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text(fixTR('STOK VE YEDEK PARCA ENVANTER RAPORU'), 14, 18);

  const todayStr = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(fixTR(`Rapor Tarihi: ${todayStr}`), 283, 14, { align: 'right' });

  // Summary Cards
  const summaryBox = (x: number, y: number, w: number, h: number, title: string, val: string, bg: [number, number, number], tc: [number, number, number]) => {
    doc.setFillColor(...bg);
    doc.roundedRect(x, y, w, h, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...tc);
    doc.text(fixTR(title), x + 3, y + 6);
    doc.setFontSize(10);
    doc.text(fixTR(val), x + 3, y + 13);
  };

  summaryBox(14, 28, 42, 16, 'TOPLAM CESIT', `${totalKinds} Parca`, [241, 245, 249], [30, 41, 59]);
  summaryBox(59, 28, 42, 16, 'TOPLAM STOK ADEDI', `${totalUnits} Adet`, [238, 242, 255], [67, 56, 202]);
  summaryBox(104, 28, 47, 16, 'TOPLAM MALIYET', formatPDFTL(totalCostVal), [254, 243, 199], [146, 64, 14]);
  summaryBox(154, 28, 47, 16, 'TOPLAM SATIS DEGERI', formatPDFTL(totalSaleVal), [236, 253, 245], [6, 95, 70]);
  summaryBox(204, 28, 42, 16, 'POTANSIYEL KAR', formatPDFTL(totalSaleVal - totalCostVal), [240, 253, 244], [21, 128, 61]);
  summaryBox(249, 28, 34, 16, 'KRITIK / TUKENEN', `${criticalCount} / ${outOfStockCount}`, [255, 241, 242], [159, 18, 57]);

  // Inventory Table
  const tableRows = items.map(item => {
    const qty = item.quantity || 0;
    const cost = item.costPrice || 0;
    const sale = item.salePrice || 0;
    const minQ = item.minQuantity || 2;
    let status = 'Yeterli';
    if (qty <= 0) status = 'TUKENDI';
    else if (qty <= minQ) status = 'KRITIK';

    return [
      fixTR(item.code || '-'),
      fixTR(item.name || '-'),
      fixTR(item.category || '-'),
      fixTR([item.brand, item.model].filter(Boolean).join(' ') || '-'),
      fixTR(item.shelfLocation || '-'),
      `${qty} Adet`,
      fixTR(status),
      formatPDFTL(cost),
      formatPDFTL(sale),
      formatPDFTL(qty * cost),
      formatPDFTL(qty * sale),
    ];
  });

  autoTable(doc, {
    startY: 48,
    margin: { left: 14, right: 14 },
    theme: 'striped',
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5 },
    styles: { fontSize: 7, cellPadding: 2, font: 'helvetica' },
    columnStyles: {
      5: { halign: 'center', fontStyle: 'bold' },
      6: { halign: 'center' },
      7: { halign: 'right' },
      8: { halign: 'right' },
      9: { halign: 'right' },
      10: { halign: 'right', fontStyle: 'bold' },
    },
    head: [[
      fixTR('KOD / BARKOD'),
      fixTR('URUN / PARCA ADI'),
      fixTR('KATEGORI'),
      fixTR('UYUMLU MODEL'),
      fixTR('RAF / KONUM'),
      fixTR('STOK'),
      fixTR('DURUM'),
      fixTR('ALIS TL'),
      fixTR('SATIS TL'),
      fixTR('TOP. MALIYET'),
      fixTR('TOP. SATIS')
    ]],
    body: tableRows.length > 0 ? tableRows : [[fixTR('Stokta kayitli urun bulunmuyor.'), '', '', '', '', '', '', '', '', '', '']],
  });

  const today = new Date().toISOString().split('T')[0];
  doc.save(`Stok_Envanter_Raporu_${today}.pdf`);
}

/**
 * Stock Usage & Deduction Report PDF Export (Kullanilan Parcalar & Stok Dusum Raporu)
 */
export function exportStockUsageReportPDF(
  periodKey: string,
  formattedPeriodName: string,
  tickets: ServiceTicket[],
  inventory: InventoryItem[] = [],
  company: CompanyInfo
) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const pw = 297; // Landscape A4 width
  let y = 12;

  // Filter tickets for period
  const filteredTickets = periodKey === 'all'
    ? tickets
    : tickets.filter(t => {
        return Boolean(t.createdAt && t.createdAt.startsWith(periodKey));
      });

  interface PartLog {
    date: string;
    ticketId: string;
    customer: string;
    device: string;
    partName: string;
    code: string;
    category: string;
    qty: number;
    uCost: number;
    uSale: number;
    tCost: number;
    tSale: number;
    profit: number;
    currentStock: number | string;
  }

  const logs: PartLog[] = [];
  const partMap = new Map<string, {
    name: string;
    code: string;
    category: string;
    totalQty: number;
    totalCost: number;
    totalSale: number;
    count: number;
    currentStock: number | string;
  }>();

  let totalQty = 0;
  let totalCost = 0;
  let totalSale = 0;

  filteredTickets.forEach(t => {
    const tDate = (t.createdAt || '').substring(0, 10);
    const deviceStr = [t.deviceType, t.deviceBrand, t.deviceModel].filter(Boolean).join(' ');

    (t.parts || []).forEach(p => {
      const qty = p.quantity || 1;
      const uCost = p.costPrice !== undefined ? p.costPrice : 0;
      const uSale = p.unitPrice || 0;
      const rowCost = qty * uCost;
      const rowSale = qty * uSale;
      const rowProfit = rowSale - rowCost;

      const invItem = p.inventoryItemId
        ? inventory.find(i => i.id === p.inventoryItemId)
        : inventory.find(i => i.name.trim().toLowerCase() === p.name.trim().toLowerCase());

      const code = invItem?.code || invItem?.id || '-';
      const category = invItem?.category || 'Genel';
      const currentStock = invItem ? (invItem.quantity ?? 0) : 'Harici';

      totalQty += qty;
      totalCost += rowCost;
      totalSale += rowSale;

      logs.push({
        date: tDate,
        ticketId: t.id,
        customer: t.customerName || '',
        device: deviceStr,
        partName: p.name,
        code,
        category,
        qty,
        uCost,
        uSale,
        tCost: rowCost,
        tSale: rowSale,
        profit: rowProfit,
        currentStock,
      });

      const aggKey = invItem ? invItem.id : p.name.trim().toLowerCase();
      const existing = partMap.get(aggKey) || {
        name: p.name,
        code,
        category,
        totalQty: 0,
        totalCost: 0,
        totalSale: 0,
        count: 0,
        currentStock,
      };
      existing.totalQty += qty;
      existing.totalCost += rowCost;
      existing.totalSale += rowSale;
      existing.count += 1;
      partMap.set(aggKey, existing);
    });
  });

  const totalProfit = totalSale - totalCost;
  const overallMargin = totalCost > 0 ? Math.round((totalProfit / totalCost) * 100) : 0;

  // Header Banner
  doc.setFillColor(15, 23, 42);
  doc.rect(10, y, pw - 20, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(fixTR(company.name).toUpperCase(), 15, y + 7.5);

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.text(fixTR(`KULLANILAN PARCALAR VE STOK DUSUM RAPORU (${formattedPeriodName.toUpperCase()})`), 15, y + 14.5);

  doc.setFontSize(8);
  doc.text(fixTR(`Tarih: ${new Date().toLocaleDateString('tr-TR')}`), pw - 15, y + 14.5, { align: 'right' });

  y += 24;

  // KPI Summary Cards
  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold', halign: 'center', fontSize: 7.5 },
    styles: { fontSize: 7.5, cellPadding: 2, font: 'helvetica', halign: 'center' },
    head: [[
      fixTR('TOPLAM PARCA ADEDI'),
      fixTR('FARKLI CESIT'),
      fixTR('PARCA MALIYETI (ALIS)'),
      fixTR('PARCA SATIS (FATURA)'),
      fixTR('PARCA NET KARI'),
      fixTR('KAR MARJI (%)'),
      fixTR('ISLEM GOREN SERVIS')
    ]],
    body: [[
      `${totalQty} Adet`,
      `${partMap.size} Kalem`,
      formatPDFTL(totalCost),
      formatPDFTL(totalSale),
      formatPDFTL(totalProfit),
      `%${overallMargin}`,
      `${filteredTickets.length} Servis`
    ]],
  });

  // @ts-expect-error autoTable lastAutoTable
  y = doc.lastAutoTable.finalY + 5;

  // Table: Detailed Log Table
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR(`DETAYLI SERVIS PARCA KULLANIM VE STOK DUSUM LISTESI (${logs.length} Adet Islem)`), 10, y);
  y += 2.5;

  const tableBody = logs.map(l => [
    l.date,
    l.ticketId,
    fixTR(l.customer),
    fixTR(l.device),
    fixTR(l.partName),
    fixTR(l.code),
    fixTR(l.category),
    `${l.qty} Adet`,
    `${l.currentStock}`,
    formatPDFTL(l.uCost),
    formatPDFTL(l.uSale),
    formatPDFTL(l.tCost),
    formatPDFTL(l.tSale),
    formatPDFTL(l.profit),
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'striped',
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
    styles: { fontSize: 6.8, cellPadding: 1.6, font: 'helvetica' },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 16, fontStyle: 'bold' },
      2: { cellWidth: 26 },
      3: { cellWidth: 30 },
      4: { cellWidth: 40, fontStyle: 'bold' },
      5: { cellWidth: 18 },
      6: { cellWidth: 18 },
      7: { cellWidth: 14, halign: 'center' },
      8: { cellWidth: 14, halign: 'center' },
      9: { halign: 'right' },
      10: { halign: 'right' },
      11: { halign: 'right' },
      12: { halign: 'right' },
      13: { halign: 'right', fontStyle: 'bold' },
    },
    head: [[
      fixTR('TARIH'),
      fixTR('SERVIS NO'),
      fixTR('MUSTERI'),
      fixTR('CIHAZ'),
      fixTR('PARCA ADI'),
      fixTR('KOD'),
      fixTR('KATEGORI'),
      fixTR('ADET'),
      fixTR('DEPO'),
      fixTR('ALIS'),
      fixTR('SATIS'),
      fixTR('TOP. ALIS'),
      fixTR('TOP. SATIS'),
      fixTR('NET KAR')
    ]],
    body: tableBody.length > 0 ? tableBody : [[fixTR('Bu donemde parca kullanimi bulunamadi.'), '', '', '', '', '', '', '', '', '', '', '', '', '']],
  });

  const safeKey = periodKey.replace(/[^a-zA-Z0-9_-]/g, '_');
  doc.save(`Stok_Dusum_Raporu_${safeKey}.pdf`);
}

export function exportTechnicianReportPDF(
  technicianName: string,
  periodTitle: string,
  tickets: ServiceTicket[],
  company: CompanyInfo,
  periodKey = 'all'
) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const pw = 297; // Landscape A4 width
  let y = 12;

  // Header Banner
  doc.setFillColor(15, 23, 42); // slate-900
  doc.roundedRect(10, y, pw - 20, 22, 2.5, 2.5, 'F');

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(fixTR(company.name || 'TEKNIK SERVIS YONETIM SISTEMI'), 16, y + 8.5);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(203, 213, 225); // slate-300
  doc.text(fixTR(`Tel: ${company.phone || '-'} | E-posta: ${company.email || '-'}`), 16, y + 14.5);

  // Right Title
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(56, 189, 248); // sky-400
  doc.text(fixTR('TEKNISYEN PERFORMANS & KAZANC RAPORU'), pw - 16, y + 8.5, { align: 'right' });

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(203, 213, 225);
  doc.text(fixTR(`Donem: ${periodTitle} | Rapor Tarihi: ${formatPDFDate(new Date().toISOString())}`), pw - 16, y + 14.5, { align: 'right' });

  y += 26;

  // Calculate Metrics for this Technician's tickets
  let totalLabor = 0;
  let totalPartsRevenue = 0;
  let totalPartsCost = 0;
  let totalSubCost = 0;
  let totalTurnover = 0;
  let totalDelivered = 0;
  let totalActive = 0;
  let totalReturned = 0;

  let totalCash = 0;
  let totalCard = 0;
  let totalTransfer = 0;
  let totalCollected = 0;
  let totalRemaining = 0;

  const ticketRows = tickets.map(t => {
    const { subtotal, vatAmount, partsTotal, grandTotal } = calculateTicketTotal(
      t.parts,
      t.laborCost,
      t.vatRate,
      t.discount,
      t.isVatIncluded
    );
    const pCost = calculateTicketPartsCost(t.parts);
    const subCost = t.subServiceCost || 0;
    const split = periodKey === 'all' ? getTicketPaymentSplit(t) : getTicketPaymentSplitForPeriod(t, periodKey);
    const paid = split.total;
    const ratio = grandTotal > 0 ? paid / grandTotal : 0;

    totalLabor += (t.laborCost || 0) * ratio;
    totalPartsRevenue += (partsTotal || 0) * ratio;
    const reportDate = getTicketReportDate(t);
    const serviceInPeriod = periodKey === 'all' || Boolean(reportDate && reportDate.startsWith(periodKey));
    if (serviceInPeriod) {
      totalPartsCost += pCost;
      totalSubCost += subCost;
    }
    totalTurnover += paid;

    if (t.status === 'TeslimEdildi') totalDelivered++;
    else if (t.status === 'IadeEdildi' || t.status === 'IadeEdilecek') totalReturned++;
    else totalActive++;

    const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
    totalCollected += paid;
    totalRemaining += remaining;

    totalCash += split.cash;
    totalCard += split.card;
    totalTransfer += split.transfer;

    const tDate = (getTicketReportDate(t) || '').substring(0, 10);
    const deviceName = [t.deviceType, t.deviceBrand, t.deviceModel].filter(Boolean).join(' ');

    return {
      date: tDate,
      id: t.id,
      customer: `${t.customerName || '-'} (${t.customerPhone || ''})`,
      device: deviceName,
      status: STATUS_LABELS[t.status] || t.status,
      laborCost: t.laborCost || 0,
      partsTotal: partsTotal || 0,
      grandTotal,
      paid,
      remaining,
      paymentMethod: formatTicketPaymentMethodDisplay(t),
      paymentStatus: t.paymentStatus === 'Odendi' ? 'Odendi' : t.paymentStatus === 'KismiOdendi' ? 'Kismi Odendi' : 'Bekliyor',
    };
  });

  // Summary KPI Cards in PDF
  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5, halign: 'center' },
    styles: { fontSize: 8, fontStyle: 'bold', halign: 'center', cellPadding: 2.2, font: 'helvetica' },
    head: [[
      fixTR('ILGILI TEKNISYEN'),
      fixTR('TOPLAM CIHAZ / SERVIS'),
      fixTR('TOPLAM HIZMET GELIRI'),
      fixTR('TOPLAM SERVIS CIROSU'),
      fixTR('KASA: NAKIT'),
      fixTR('KASA: KREDI KARTI'),
      fixTR('KASA: HAVALE/EFT'),
      fixTR('TOPLAM TAHSILAT'),
      fixTR('KALAN ALACAK')
    ]],
    body: [[
      fixTR(technicianName),
      `${tickets.length} Cihaz (${totalDelivered} Teslim)`,
      formatPDFTL(totalLabor),
      formatPDFTL(totalTurnover),
      formatPDFTL(totalCash),
      formatPDFTL(totalCard),
      formatPDFTL(totalTransfer),
      formatPDFTL(totalCollected),
      formatPDFTL(totalRemaining)
    ]],
  });

  // @ts-expect-error autoTable lastAutoTable
  y = doc.lastAutoTable.finalY + 5;

  // Table: Detailed Ticket List
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(fixTR(`TEKNISYENIN BAKTIGI CIHAZLAR VE ISLEM DETAYLARI (${ticketRows.length} Servis Kaydi)`), 10, y);
  y += 2.5;

  const tableBody = ticketRows.map(r => [
    r.date,
    r.id,
    fixTR(r.customer),
    fixTR(r.device),
    fixTR(r.status),
    formatPDFTL(r.laborCost),
    formatPDFTL(r.partsTotal),
    formatPDFTL(r.grandTotal),
    formatPDFTL(r.paid),
    formatPDFTL(r.remaining),
    fixTR(r.paymentMethod),
    fixTR(r.paymentStatus)
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: 10, right: 10 },
    theme: 'striped',
    headStyles: { fillColor: [51, 65, 85], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.8 },
    styles: { fontSize: 6.5, cellPadding: 1.5, font: 'helvetica' },
    columnStyles: {
      0: { cellWidth: 16 },
      1: { cellWidth: 16, fontStyle: 'bold' },
      2: { cellWidth: 38 },
      3: { cellWidth: 38 },
      4: { cellWidth: 20 },
      5: { halign: 'right' },
      6: { halign: 'right' },
      7: { halign: 'right', fontStyle: 'bold' },
      8: { halign: 'right' },
      9: { halign: 'right' },
      10: { cellWidth: 24 },
      11: { cellWidth: 18, halign: 'center' },
    },
    head: [[
      fixTR('TARIH'),
      fixTR('SERVIS NO'),
      fixTR('MUSTERI & TEL'),
      fixTR('CIHAZ MODEL'),
      fixTR('DURUM'),
      fixTR('HIZMET / ISLEM'),
      fixTR('PARCA'),
      fixTR('TOPLAM'),
      fixTR('TAHSILAT'),
      fixTR('KALAN'),
      fixTR('ODEME TURU'),
      fixTR('ODEME DURUMU')
    ]],
    body: tableBody.length > 0 ? tableBody : [[fixTR('Bu donemde teknisyene ait servis kaydi bulunamadi.'), '', '', '', '', '', '', '', '', '', '', '']],
  });

  const safeTech = technicianName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const safePeriod = periodTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
  doc.save(`Teknisyen_Raporu_${safeTech}_${safePeriod}.pdf`);
}





// Cihaz alanı aylık gelir raporu: Yazıcı/Tarayıcı ve Bilgisayar
export const exportDeviceAreaMonthlyReportPDF = (
  selectedMonthKey: string,
  formattedMonthName: string,
  tickets: ServiceTicket[],
  companyInfo: CompanyInfo
) => {
  const doc = new jsPDF();
  const eligibleTickets = tickets.filter(t => {
    if (t.isDeleted) return false;
    if (t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId)) return false;
    return true;
  });
  const monthTickets = eligibleTickets.filter(t => Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(selectedMonthKey)));

  const areas = {
    printer: { label: 'Yazici / Tarayici', count: 0, revenue: 0, collected: 0, remaining: 0 },
    computer: { label: 'Bilgisayar', count: 0, revenue: 0, collected: 0, remaining: 0 },
  };

  monthTickets.forEach(t => {
    const type = t.deviceType || '';
    const lower = type.toLocaleLowerCase('tr-TR');
    const inferred: 'printer' | 'computer' =
      lower.includes('yazıcı') || lower.includes('yazici') ||
      lower.includes('tarayıcı') || lower.includes('tarayici') ||
      lower.includes('printer') || lower.includes('scanner') ||
      lower.includes('fotokopi') ? 'printer' : 'computer';
    const area = companyInfo.deviceAreaAssignments?.[type] || inferred;
    const { grandTotal } = calculateTicketTotal(
      t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0
    );
    const allTimePaid = getTicketPaymentSplit(t).total;
    areas[area].remaining += Math.max(0, grandTotal - allTimePaid);
  });

  eligibleTickets.forEach(t => {
    const paidInPeriod = getTicketPaymentSplitForPeriod(t, selectedMonthKey).total;
    if (paidInPeriod <= 0) return;
    const type = t.deviceType || '';
    const lower = type.toLocaleLowerCase('tr-TR');
    const inferred: 'printer' | 'computer' =
      lower.includes('yazıcı') || lower.includes('yazici') ||
      lower.includes('tarayıcı') || lower.includes('tarayici') ||
      lower.includes('printer') || lower.includes('scanner') ||
      lower.includes('fotokopi') ? 'printer' : 'computer';
    const area = companyInfo.deviceAreaAssignments?.[type] || inferred;
    areas[area].count += 1;
    areas[area].revenue += paidInPeriod;
    areas[area].collected += paidInPeriod;
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(fixTR(companyInfo.name || 'Teknik Servis'), 14, 16);
  doc.setFontSize(13);
  doc.text('CIHAZ ALANI AYLIK GELIR RAPORU', 14, 25);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(fixTR(`Donem: ${formattedMonthName}`), 14, 32);
  doc.text(fixTR(`Olusturma: ${new Date().toLocaleString('tr-TR')}`), 14, 38);

  autoTable(doc, {
    startY: 46,
    head: [['Cihaz Alani', 'Tahsilat Alan Form', 'Alinan Tahsilat', 'Kalan Alacak']],
    body: [
      [areas.printer.label, String(areas.printer.count), formatPDFTL(areas.printer.revenue), formatPDFTL(areas.printer.remaining)],
      [areas.computer.label, String(areas.computer.count), formatPDFTL(areas.computer.revenue), formatPDFTL(areas.computer.remaining)],
      ['GENEL TOPLAM', String(areas.printer.count + areas.computer.count),
        formatPDFTL(areas.printer.revenue + areas.computer.revenue),
        formatPDFTL(areas.printer.remaining + areas.computer.remaining)]
    ],
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 3 },
    headStyles: { fontStyle: 'bold' },
    footStyles: { fontStyle: 'bold' },
  });

  const detailRows = eligibleTickets.flatMap(ticket => {
    const explicitPayments = Array.isArray(ticket.payments) ? ticket.payments : [];
    if (explicitPayments.length) return explicitPayments
      .filter(payment => payment.date?.startsWith(selectedMonthKey) && Number(payment.amount) > 0)
      .map(payment => ({ ticket, date: payment.date || '', amount: Number(payment.amount) || 0 }));
    if (!ticket.paymentDate?.startsWith(selectedMonthKey)) return [];
    return [{ ticket, date: ticket.paymentDate, amount: getTicketPaymentSplitForPeriod(ticket, selectedMonthKey).total }];
  }).filter(receipt => receipt.amount > 0).map(({ ticket: t, date, amount }) => {
    const type = t.deviceType || '';
    const lower = type.toLocaleLowerCase('tr-TR');
    const inferred: 'printer' | 'computer' =
      lower.includes('yazıcı') || lower.includes('yazici') ||
      lower.includes('tarayıcı') || lower.includes('tarayici') ||
      lower.includes('printer') || lower.includes('scanner') ||
      lower.includes('fotokopi') ? 'printer' : 'computer';
    const area = companyInfo.deviceAreaAssignments?.[type] || inferred;
    return [
      fixTR(formatPDFDate(date)),
      t.id || '-',
      fixTR(type || '-'),
      area === 'printer' ? 'Yazici / Tarayici' : 'Bilgisayar',
      fixTR(t.customerName || '-'),
      formatPDFTL(amount),
    ];
  });

  if (detailRows.length > 0) {
    const lastY = (doc as any).lastAutoTable?.finalY || 80;
    autoTable(doc, {
      startY: lastY + 10,
      head: [['Odeme Tarihi', 'Form No', 'Cihaz Turu', 'Alan', 'Musteri', 'Alinan Tahsilat']],
      body: detailRows,
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 2.5 },
      headStyles: { fontStyle: 'bold' },
    });
  }

  doc.save(`cihaz-alani-gelir-raporu-${selectedMonthKey}.pdf`);
};

export const exportPrinterMonthlyReportPDF = (
  monthKey: string,
  companyName: string,
  rows: Array<{ label: string; value: string }>,
  receiptTickets: ServiceTicket[] = []
) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(fixTR(companyName || 'Teknik Servis'), 14, 16);
  doc.setFontSize(13);
  doc.text(fixTR('YAZICI AYLIK RAPORU'), 14, 25);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(fixTR(`Donem: ${monthKey}`), 14, 33);
  doc.text(fixTR(`Olusturma: ${new Date().toLocaleString('tr-TR')}`), 14, 39);

  autoTable(doc, {
    startY: 47,
    head: [rows.map(row => fixTR(row.label))],
    body: [rows.map(row => fixTR(row.value.replace(/\u00a0/g, ' ').replace(/₺/g, 'TL')))],
    theme: 'grid',
    tableWidth: 'auto',
    margin: { left: 10, right: 10 },
    styles: { font: 'helvetica', fontSize: 6.5, cellPadding: 2, overflow: 'linebreak', halign: 'center', valign: 'middle' },
    headStyles: { fontStyle: 'bold', fillColor: [30, 64, 175] },
    bodyStyles: { fontStyle: 'bold', textColor: [15, 23, 42] },
  });

  let finalY = (doc as any).lastAutoTable?.finalY || 80;
  const receiptRows: Array<[string, string, string, string, string, string]> = [];
  receiptTickets.forEach(ticket => {
    const payments = Array.isArray(ticket.payments) ? ticket.payments : [];
    if (payments.length) {
      payments.forEach(payment => {
        const amount = Math.max(0, Number(payment.amount) || 0);
        if (amount > 0 && payment.date?.startsWith(monthKey)) receiptRows.push([
          fixTR(formatPDFDate(payment.date)), fixTR(ticket.id), fixTR(ticket.customerName || '-'),
          fixTR(`${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim()),
          fixTR(String(payment.method || 'Nakit')), formatPDFTL(amount),
        ]);
      });
      return;
    }
    if (!ticket.paymentDate?.startsWith(monthKey)) return;
    const split = getTicketPaymentSplitForPeriod(ticket, monthKey);
    ( [['Nakit', split.cash], ['Kredi Karti', split.card], ['EFT/Havale', split.transfer]] as Array<[string, number]> ).forEach(([method, amount]) => {
      if (amount > 0) receiptRows.push([
        fixTR(formatPDFDate(ticket.paymentDate)), fixTR(ticket.id), fixTR(ticket.customerName || '-'),
        fixTR(`${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim()),
        fixTR(method), formatPDFTL(amount),
      ]);
    });
  });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(fixTR('TAHSILAT HAREKETLERI - ODEME TARIHI VE SERVIS FORMU'), 14, finalY + 10);
  autoTable(doc, {
    startY: finalY + 13,
    head: [[fixTR('ODEME TARIHI'), fixTR('SERVIS FORMU'), fixTR('MUSTERI'), fixTR('CIHAZ'), fixTR('ODEME TURU'), fixTR('ALINAN TUTAR')]],
    body: receiptRows.length ? receiptRows : [[fixTR(monthKey), '', fixTR('Bu ay tahsilat kaydi yok'), '', '', formatPDFTL(0)]],
    theme: 'striped', margin: { left: 10, right: 10 },
    headStyles: { fillColor: [5, 150, 105], textColor: 255, fontStyle: 'bold' },
    styles: { font: 'helvetica', fontSize: 7.5, cellPadding: 2, overflow: 'linebreak' },
  });
  finalY = (doc as any).lastAutoTable?.finalY || finalY;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const note = fixTR('Ay icinde alinan tutarlar odeme tarihine gore, alinacak tutar ise secilen aydaki servis formlarinin kalan bakiyesi olarak gosterilir. Yazici servis geliri tahsilatin yapildigi ayda kaydedilir. Garanti kategorilerindeki tutarlar KDV haric orantili tahsilattir. Net ciro = garantili KDV siz + garantisiz KDV siz + dis servis geliri KDV siz + hakedis KDV siz - kullanilan parca maliyeti - sehir disi servis giderleri. Stok TL degeri guncel agirlikli ortalama birim maliyetle hesaplanir.');
  const wrapped = doc.splitTextToSize(note, 275);
  doc.text(wrapped, 14, finalY + 10);
  doc.save(`Yazici_Aylik_Raporu_${monthKey}.pdf`);
};
